import { useMemo } from 'react';
import { STEPS, STEP_COLOR as SHARED_STEP_COLOR, STEP_OF_STAGE, NEXT_TO_STEP } from '@/lib/steps';
import { Link } from 'react-router-dom';
import { Video, Image as ImageIcon, Layout, Camera, Type, BookOpen, CheckSquare } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CalendarEvent } from '@/lib/queries';

interface Props {
  monthStart: Date;       // first day of the month being displayed
  events: CalendarEvent[];
}

const TYPE_ICON: Record<string, React.ReactNode> = {
  video: <Video size={10} />, reel: <Video size={10} />,
  static: <ImageIcon size={10} />, carousel: <Layout size={10} />,
  story: <BookOpen size={10} />, photo: <Camera size={10} />, copy: <Type size={10} />,
};

/** Re-exported so the calendar legend and every filter read one list (lib/steps.ts). */
export const STEP_STYLE = STEPS.map((s) => ({ key: s.key, label: s.label, color: s.color }));
const STEP_COLOR: Record<string, string> = SHARED_STEP_COLOR;
/** The tracker's next-action keys fold onto the steps above (lib/steps.ts). */
const STEP_ALIAS: Record<string, string> = { ...NEXT_TO_STEP, brief: 'script', post: 'posted' };
const STAGE_FALLBACK: Record<string, string> = STEP_OF_STAGE;
const TASK_COLOR: Record<string, string> = {
  pending:     'bg-slate-700/40 text-slate-300 border-slate-600/40',
  in_progress: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  submitted:   'bg-purple-500/15 text-purple-300 border-purple-500/30',
  done:        'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  blocked:     'bg-red-500/15 text-red-300 border-red-500/30',
  cancelled:   'bg-slate-800/40 text-slate-500 border-slate-700/40',
};
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function MonthGrid({ monthStart, events }: Props) {
  const cells = useMemo(() => buildCells(monthStart), [monthStart]);
  const eventsByDay = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      if (!m.has(e.date)) m.set(e.date, []);
      m.get(e.date)!.push(e);
    }
    return m;
  }, [events]);

  const today = new Date().toISOString().slice(0, 10);
  const monthIdx = monthStart.getMonth();

  return (
    <div className="rounded-xl border border-slate-800 overflow-hidden">
      {/* DOW header */}
      <div className="grid grid-cols-7 bg-bg-card border-b border-slate-800">
        {DOW.map((d) => (
          <div key={d} className="text-[10px] text-muted uppercase tracking-wider text-center py-2">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 auto-rows-[minmax(110px,auto)]">
        {cells.map((iso) => {
          const date = new Date(iso + 'T00:00:00Z');
          const dayN = date.getUTCDate();
          const inMonth = date.getUTCMonth() === monthIdx;
          const isToday = iso === today;
          const dayEvents = eventsByDay.get(iso) ?? [];

          return (
            <div
              key={iso}
              className={cn(
                'border-r border-b border-slate-800 last:border-r-0 p-1.5 flex flex-col gap-1 min-h-[110px]',
                inMonth ? 'bg-bg-card/40' : 'bg-bg-card/10',
                isToday && 'ring-1 ring-accent-400/60 ring-inset'
              )}
            >
              <div className={cn(
                'text-[10px] font-mono self-end',
                inMonth ? 'text-muted-strong' : 'text-muted/40',
                isToday && 'text-accent-400 font-semibold'
              )}>
                {dayN}
              </div>
              {dayEvents.slice(0, 4).map((e) => (
                <EventChip key={e.id} e={e} />
              ))}
              {dayEvents.length > 4 && (
                <div className="text-[9px] text-muted text-center">+{dayEvents.length - 4} more</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventChip({ e }: { e: CalendarEvent }) {
  if (e.kind === 'shoot') {
    const cls = e.state === 'cancelled' ? 'bg-slate-800/40 text-slate-500 border-slate-700/40 line-through' : 'bg-pink-500/15 text-pink-300 border-pink-500/30';
    return (
      <Link to={e.shoot_id ? `/shoots/${e.shoot_id}` : '/shoots'} className={cn('block rounded px-1.5 py-0.5 text-[10px] border truncate', cls)}
        title={`${e.time ?? ''} ${e.title} (${e.client_name ?? ''})${e.location ? ' at ' + e.location : ''}${e.crew_names?.length ? ' with ' + e.crew_names.join(', ') : ''} · ${e.creative_count ?? 0} creatives`}>
        <span className="inline-flex items-center gap-1"><Camera size={9} /><span className="truncate">{e.time ? e.time + ' ' : ''}{e.title}</span></span>
      </Link>
    );
  }
  if (e.kind === 'task') {
    const color = TASK_COLOR[e.task_status ?? 'pending'];
    return (
      <Link
        to={e.creative_id ? `/creatives/${e.creative_id}` : '#'}
        className={cn('block rounded px-1.5 py-0.5 text-[10px] border truncate', color)}
        title={`${e.title} - ${e.assignee_name ?? 'unassigned'}${e.task_priority && e.task_priority !== 'normal' ? ` [${e.task_priority}]` : ''}`}
      >
        <span className="inline-flex items-center gap-1">
          <CheckSquare size={9} />
          <span className="truncate">{e.title}</span>
        </span>
      </Link>
    );
  }

  const stepKey = STEP_ALIAS[e.step_key ?? ''] ?? e.step_key ?? STAGE_FALLBACK[e.current_stage ?? 'brief'] ?? 'script';
  const color = STEP_COLOR[stepKey] ?? STEP_COLOR.script;
  return (
    <Link
      to={e.creative_id ? `/creatives/${e.creative_id}` : '#'}
      className={cn('block rounded px-1.5 py-0.5 text-[10px] border truncate', color)}
      title={`${e.code ?? ''} ${e.title}${e.client_name ? ` (${e.client_name})` : ''}${e.step_label ? ` · ${e.step_label}` : ''}${e.step_owner ? ` · ${e.step_owner}` : ''}`}
    >
      <span className="inline-flex items-center gap-1">
        {e.creative_type && TYPE_ICON[e.creative_type]}
        <span className="truncate">{e.title}</span>
      </span>
    </Link>
  );
}

function buildCells(monthStart: Date): string[] {
  // Build 42 cells (6 weeks) starting from Monday before month-start
  const first = new Date(Date.UTC(monthStart.getFullYear(), monthStart.getMonth(), 1));
  // 0 = Sun ... 6 = Sat. We want Monday-first.
  const dow = (first.getUTCDay() + 6) % 7;
  const gridStart = new Date(Date.UTC(first.getFullYear(), first.getMonth(), 1 - dow));
  const cells: string[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setUTCDate(gridStart.getUTCDate() + i);
    cells.push(d.toISOString().slice(0, 10));
  }
  return cells;
}
